import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";
import { Toaster } from "sonner";
import useVisitTracker from "@/hooks/useVisitTracker";
import WhatsAppButton from "@/components/WhatsAppButton";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import Checkout from "@/pages/Checkout";
import Cart from "@/pages/Cart";
import ClientLayout from "@/components/Layout/ClientLayout";
import AdminLayout from "@/components/Layout/AdminLayout";
import Dashboard from "@/pages/Dashboard";
import VpsList from "@/pages/VpsList";
import VpsDetail from "@/pages/VpsDetail";
import Invoices from "@/pages/Invoices";
import InvoiceDetail from "@/pages/InvoiceDetail";
import Tickets from "@/pages/Tickets";
import TicketDetail from "@/pages/TicketDetail";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminAnalytics from "@/pages/AdminAnalytics";
import AdminPlans from "@/pages/AdminPlans";
import AdminCoupons from "@/pages/AdminCoupons";
import AdminClients from "@/pages/AdminClients";
import AdminResellers from "@/pages/AdminResellers";
import AdminInvoices from "@/pages/AdminInvoices";
import AdminVpsPage from "@/pages/AdminVpsPage";
import AdminTickets from "@/pages/AdminTickets";
import ResellerDashboard from "@/pages/ResellerDashboard";
import ResellerLayout from "@/components/Layout/ResellerLayout";
import { homePathForRole } from "@/lib/authRouting";
import AdminMonitoring from "@/pages/AdminMonitoring";

function Protected({ children, adminOnly = false, resellerOnly = false }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-zinc-500">Carregando…</div>;
  }
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  if (adminOnly && user.role !== "admin") return <Navigate to={homePathForRole(user.role)} replace />;
  if (resellerOnly && user.role !== "reseller") return <Navigate to={homePathForRole(user.role)} replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  useVisitTracker();  // registra pageview em cada mudança de rota (ignora /admin)
  // Detect Emergent OAuth session fragment synchronously before other routes render
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Login initialTab="register" />} />
      <Route path="/auth/callback" element={<AuthCallback />} />

      <Route path="/checkout/:planId" element={<Protected><Checkout /></Protected>} />
      <Route path="/cart" element={<Cart />} />

      <Route path="/dashboard" element={<Protected><ClientLayout /></Protected>}>
        <Route index element={<Dashboard />} />
        <Route path="vps" element={<VpsList />} />
        <Route path="vps/:vpsId" element={<VpsDetail />} />
        <Route path="invoices" element={<Invoices />} />
        <Route path="invoices/:invoiceId" element={<InvoiceDetail />} />
        <Route path="tickets" element={<Tickets />} />
        <Route path="tickets/:ticketId" element={<TicketDetail />} />
      </Route>

      <Route path="/reseller" element={<Protected resellerOnly><ResellerLayout /></Protected>}>
        <Route index element={<ResellerDashboard />} />
      </Route>

      <Route path="/admin" element={<Protected adminOnly><AdminLayout /></Protected>}>
        <Route index element={<AdminDashboard />} />
        <Route path="analytics" element={<AdminAnalytics />} />
        <Route path="plans" element={<AdminPlans />} />
        <Route path="coupons" element={<AdminCoupons />} />
        <Route path="clients" element={<AdminClients />} />
        <Route path="resellers" element={<AdminResellers />} />
        <Route path="invoices" element={<AdminInvoices />} />
        <Route path="vps" element={<AdminVpsPage />} />
        <Route path="monitoring" element={<AdminMonitoring />} />
        <Route path="tickets" element={<AdminTickets />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <CartProvider>
        <BrowserRouter>
          <AppRouter />
          <WhatsAppButton />
          <Toaster theme="dark" position="top-right" richColors />
        </BrowserRouter>
      </CartProvider>
    </AuthProvider>
  );
}
